/* Figma-derived header component adapted to project Tailwind setup */
import React from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import { initAmplify } from "../../../lib/amplify";
import {
  signOutPatientWithMediaCookieRevoke,
  signOutWithMediaCookieRevoke,
} from "../../../lib/logout";
import { isPatientSelfAuthEnabled } from "../../../lib/featureFlags";
import {
  groupsOfAmplifySession,
  hasStaffGroup,
} from "../../../lib/staffGroups";
import { currentPatientSession } from "../../../features/patient-auth";

import iconButtonSvg from "../../../assets/ProfileIcon/IconButton_Manager.svg";
import brandLogoSvg from "../../../assets/Icon/Logo.svg";
import { Button } from "../Button/Button";

/**
 * 右側の表示（Issue #473）。「ログイン」のボタンは出さない（ログインはトップの入口から）。上から順に決める。
 * - Amplify のセッションがスタッフのグループを持つ: 名前とログアウト（Cognito のログアウト）
 * - 患者のフラグが ON で、SAS の患者のセッションがある: 「ログイン中」とログアウト（SAS のアクセストークンは氏名を持たない）
 * - 患者のフラグが ON で、Amplify のセッションが patient-user だけ: 何も出さない
 * - 患者のフラグが OFF で、Amplify のセッションがある: 名前とログアウト（今のまま）
 * - どのセッションも無い: 何も出さない
 */
type SignedIn = { kind: "none" } | { kind: "cognito"; name: string } | {
  kind: "patient";
};

const displayNameOf = (claims: any): string => {
  const username = claims["cognito:username"] || claims.preferred_username;
  return (
    username ||
    claims.name ||
    (claims.given_name && claims.family_name
      ? `${claims.family_name} ${claims.given_name}`
      : claims.email || "ログイン済み")
  );
};

async function resolveSignedIn(): Promise<SignedIn> {
  let session: Awaited<ReturnType<typeof fetchAuthSession>> | null = null;
  try {
    session = await fetchAuthSession();
  } catch {
    session = null;
  }
  const hasIdToken = Boolean(session?.tokens?.idToken);
  const cognito = (): SignedIn => ({
    kind: "cognito",
    name: displayNameOf(session?.tokens?.idToken?.payload ?? {}),
  });
  if (hasIdToken && hasStaffGroup(groupsOfAmplifySession(session))) {
    return cognito();
  }
  if (isPatientSelfAuthEnabled()) {
    return currentPatientSession() !== null
      ? { kind: "patient" }
      : { kind: "none" };
  }
  return hasIdToken ? cognito() : { kind: "none" };
}

export function Header() {
  initAmplify();
  const [signedIn, setSignedIn] = React.useState<SignedIn>({ kind: "none" });

  React.useEffect(() => {
    let mounted = true;
    void resolveSignedIn().then((next) => {
      if (mounted) setSignedIn(next);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const onLogout = async () => {
    try {
      if (signedIn.kind === "patient") {
        await signOutPatientWithMediaCookieRevoke();
      } else {
        await signOutWithMediaCookieRevoke();
      }
    } catch {
      alert("ログアウトに失敗しました。");
    }
  };

  return (
    <header
      className="bg-brandBlue w-full h-header flex items-center px-s-600 py-s-300"
      data-name="Header"
      data-node-id="979:25874"
    >
      {/* Left block */}
      <div
        className="flex items-center gap-s-100 flex-1 min-w-px min-h-px"
        data-node-id="979:25810"
      >
        <div
          className="w-[40px] h-[27px] relative shrink-0"
          data-name="Figma"
          data-node-id="979:25813"
        >
          <img
            alt="logo"
            className="block max-w-none w-full h-full"
            src={brandLogoSvg}
          />
        </div>
        <p
          className="text-brandOnBrandText text-lg font-medium"
          data-node-id="979:25815"
        >
          SASオンラインクリニック
        </p>
        <div className="px-[10px]" data-node-id="979:25868">
          <p
            className="text-brandOnBrandText text-base tracking-tiny"
            data-node-id="979:25869"
          >
            NTT西日本
          </p>
        </div>
      </div>

      {/* Right block */}
      <div
        className="flex items-center justify-end gap-s-250 h-[39px]"
        data-node-id="979:25817"
      >
        {signedIn.kind !== "none" && (
          <>
            <div
              className="rounded-pill size-[33px] overflow-clip flex items-center justify-center"
              data-name="Icon Button"
              data-node-id="979:25818"
            >
              <img
                alt="avatar"
                className="w-full h-full object-cover"
                src={iconButtonSvg}
              />
            </div>
            <p className="text-brandOnBrandText text-sm">
              {signedIn.kind === "patient" ? "ログイン中" : signedIn.name}
            </p>
            <Button
              label="ログアウト"
              variant="solid"
              tone="brand"
              onClick={onLogout}
              dataName="LogoutButton"
              dataNodeId="ui:logout"
            />
          </>
        )}
      </div>
    </header>
  );
}

export default Header;
